import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "./AuthContext";

// ============ AFFILIATE MARKETING SYSTEM ============

export type AffiliateStatus = "pending" | "active" | "suspended";
export type AffiliateCommissionStatus = "pending" | "approved" | "paid";
export type AffiliatePayoutStatus = "requested" | "processing" | "completed";

export const AFFILIATE_PLAN_COMMISSIONS: Record<string, { amount: number; label: string }> = {
  starter: { amount: 150, label: "Starter Plan" },
  professional: { amount: 300, label: "Professional Plan" },
  enterprise: { amount: 750, label: "Enterprise Plan" },
};

export interface AffiliateProfile {
  id: string;
  userId: string;
  name: string;
  email: string;
  code: string;
  link: string;
  status: AffiliateStatus;
  joinedAt: string;
}

export interface AffiliateReferral {
  id: string;
  affiliateId: string;
  refereeName: string;
  refereeEmail: string;
  status: "clicked" | "signed_up" | "purchased";
  plan?: string;
  createdAt: string;
  signedUpAt?: string;
  purchasedAt?: string;
}

export interface AffiliateCommission {
  id: string;
  affiliateId: string;
  referralId: string;
  refereeName: string;
  plan: string;
  amount: number;
  status: AffiliateCommissionStatus;
  month: string;
  createdAt: string;
  paidAt?: string;
}

export interface AffiliatePayout {
  id: string;
  affiliateId: string;
  amount: number;
  status: AffiliatePayoutStatus;
  method: "bank_transfer" | "upi" | "platform_credits";
  requestedAt: string;
  completedAt?: string;
  ref?: string;
}

export interface AffiliateStats {
  totalClicks: number;
  totalSignups: number;
  totalPurchases: number;
  conversionRate: number;
  totalEarned: number;
  pendingBalance: number;
  totalPaid: number;
  monthlyEarnings: number;
}

// Seed
const AFF_ID = "aff-user2";

const SEED_REFERRALS: AffiliateReferral[] = [];
const SEED_COMMISSIONS: AffiliateCommission[] = [];
const SEED_PAYOUTS: AffiliatePayout[] = [];

interface AffiliateContextType {
  profile: AffiliateProfile;
  referrals: AffiliateReferral[];
  commissions: AffiliateCommission[];
  payouts: AffiliatePayout[];
  stats: AffiliateStats;
  requestPayout: (amount: number, method: AffiliatePayout["method"]) => boolean;
}

const AffiliateContext = createContext<AffiliateContextType | null>(null);

export function AffiliateProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [referrals] = useState<AffiliateReferral[]>(SEED_REFERRALS);
  const [commissions] = useState<AffiliateCommission[]>(SEED_COMMISSIONS);
  const [payouts, setPayouts] = useState<AffiliatePayout[]>(SEED_PAYOUTS);

  const code = user ? `aff-${user.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8)}` : "aff-guest";
  const link = `accoupro.com/affiliate/${code}`;

  const affProfile: AffiliateProfile = {
    id: AFF_ID,
    userId: user?.id || "",
    name: user?.name || "",
    email: user?.email || "",
    code,
    link,
    status: "active",
    joinedAt: "2024-12-01T00:00:00Z",
  };

  const totalEarned = commissions.reduce((s, c) => s + c.amount, 0);
  const totalPaid = payouts.filter(p => p.status === "completed").reduce((s, p) => s + p.amount, 0);
  const pendingBalance = totalEarned - totalPaid;
  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthlyEarnings = commissions.filter(c => c.month === currentMonth).reduce((s, c) => s + c.amount, 0);

  const totalClicks = referrals.length;
  const totalSignups = referrals.filter(r => r.status !== "clicked").length;
  const totalPurchases = referrals.filter(r => r.status === "purchased").length;

  const stats: AffiliateStats = {
    totalClicks,
    totalSignups,
    totalPurchases,
    conversionRate: totalClicks > 0 ? Math.round((totalPurchases / totalClicks) * 100) : 0,
    totalEarned,
    pendingBalance,
    totalPaid,
    monthlyEarnings,
  };

  const requestPayout = useCallback((amount: number, method: AffiliatePayout["method"]): boolean => {
    if (amount <= 0 || amount > pendingBalance || !user) return false;
    const payout: AffiliatePayout = {
      id: `ap-${crypto.randomUUID().slice(0, 8)}`,
      affiliateId: AFF_ID,
      amount,
      status: "requested",
      method,
      requestedAt: new Date().toISOString(),
    };
    setPayouts(prev => [payout, ...prev]);
    return true;
  }, [pendingBalance, user]);

  return (
    <AffiliateContext.Provider value={{ profile: affProfile, referrals, commissions, payouts, stats, requestPayout }}>
      {children}
    </AffiliateContext.Provider>
  );
}

export function useAffiliate() {
  const ctx = useContext(AffiliateContext);
  if (!ctx) throw new Error("useAffiliate must be used within AffiliateProvider");
  return ctx;
}
